import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Mail, Send, Trash2, AlertCircle, CheckCircle2, Clock, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';

const NotificationsPanel = () => {
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, [filter]);

  const loadData = async () => {
    try {
      const [notifRes, statsRes] = await Promise.all([
        api.get('/notifications/emails', { params: filter !== 'all' ? { statut: filter } : {} }),
        api.get('/notifications/stats')
      ]);
      setNotifications(notifRes.data);
      setStats(statsRes.data);
    } catch (error) {
      toast.error('Erreur de chargement des notifications');
    } finally {
      setLoading(false);
    }
  };

  const handleSendReminders = async () => {
    setSending(true);
    try {
      await api.post('/notifications/send-journal-reminders');
      toast.success('Rappels Journal de Démarches envoyés !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des rappels');
    } finally {
      setSending(false);
    }
  };

  const handleSendD1Reminders = async () => {
    setSending(true);
    try {
      await api.post('/notifications/send-d1-reminders');
      toast.success('Rappels D-1 (événements de demain) envoyés !');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'envoi des rappels D-1');
    } finally {
      setSending(false);
    }
  };

  const handleDeleteNotification = async (id) => {
    try {
      await api.delete(`/notifications/emails/${id}`);
      toast.success('Notification supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur de suppression');
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'envoye':
        return <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Envoyé</Badge>;
      case 'erreur':
        return <Badge className="bg-red-100 text-red-700"><AlertCircle className="w-3 h-3 mr-1" />Erreur</Badge>;
      default:
        return <Badge className="bg-amber-100 text-amber-700"><Clock className="w-3 h-3 mr-1" />En attente</Badge>;
    }
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  if (loading) {
    return (
      <Card className="bg-white border-slate-200 rounded-3xl p-6 shadow-sm">
        <CardContent className="text-center py-8">
          <div className="text-slate-500">Chargement...</div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200 rounded-2xl p-4">
          <div className="text-blue-600 font-bold text-2xl">{stats?.total || 0}</div>
          <div className="text-blue-700 text-sm">Total</div>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-amber-100 border-amber-200 rounded-2xl p-4">
          <div className="text-amber-600 font-bold text-2xl">{stats?.en_attente || 0}</div>
          <div className="text-amber-700 text-sm">En attente</div>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 border-emerald-200 rounded-2xl p-4">
          <div className="text-emerald-600 font-bold text-2xl">{stats?.envoyes || 0}</div>
          <div className="text-emerald-700 text-sm">Envoyés</div>
        </Card>
        <Card className="bg-gradient-to-br from-red-50 to-red-100 border-red-200 rounded-2xl p-4">
          <div className="text-red-600 font-bold text-2xl">{stats?.erreurs || 0}</div>
          <div className="text-red-700 text-sm">Erreurs</div>
        </Card>
      </div>

      {/* Email Configuration Status */}
      {stats?.email_configured ? (
        <Card className="bg-emerald-50 border-emerald-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            <div>
              <p className="font-bold text-emerald-900">Service Email configuré</p>
              <p className="text-sm text-emerald-700">
                Envoi via Microsoft 365 : {stats.email_from}
              </p>
            </div>
          </div>
        </Card>
      ) : (
        <Card className="bg-amber-50 border-amber-200 rounded-2xl p-4">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-amber-600" />
            <div>
              <p className="font-bold text-amber-900">Service Email non configuré</p>
              <p className="text-sm text-amber-700">
                Les emails sont simulés. Configurez les credentials Azure dans le fichier .env pour activer l'envoi réel.
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* Actions */}
      <Card className="bg-white border-slate-200 rounded-3xl shadow-sm">
        <CardHeader className="flex flex-row items-center justify-between pb-4">
          <CardTitle className="text-xl font-bold flex items-center gap-2" style={{ fontFamily: 'Nunito, sans-serif' }}>
            <Mail className="w-5 h-5" />
            Notifications Email
          </CardTitle>
          <div className="flex gap-2 flex-wrap">
            <Button
              onClick={loadData}
              variant="outline"
              size="sm"
              className="rounded-xl"
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualiser
            </Button>
            <Button
              onClick={handleSendReminders}
              disabled={sending}
              variant="outline"
              className="rounded-xl border-blue-200 text-blue-700 hover:bg-blue-50"
              size="sm"
            >
              <Send className="w-4 h-4 mr-1" />
              {sending ? 'Envoi...' : 'Rappels Journal'}
            </Button>
            <Button
              onClick={handleSendD1Reminders}
              disabled={sending}
              className="bg-amber-500 hover:bg-amber-600 rounded-xl"
              size="sm"
            >
              <Clock className="w-4 h-4 mr-1" />
              {sending ? 'Envoi...' : 'Rappels D-1'}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-2 mb-4">
            {['all', 'en_attente', 'envoye', 'erreur'].map((f) => (
              <Button
                key={f}
                variant={filter === f ? 'default' : 'outline'}
                size="sm"
                onClick={() => setFilter(f)}
                className={`rounded-lg ${filter === f ? 'bg-blue-600' : ''}`}
              >
                {f === 'all' ? 'Tous' : f === 'en_attente' ? 'En attente' : f === 'envoye' ? 'Envoyés' : 'Erreurs'}
              </Button>
            ))}
          </div>

          {/* Notifications List */}
          <div className="space-y-3 max-h-[400px] overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Mail className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                <p>Aucune notification</p>
              </div>
            ) : (
              notifications.map((notif) => (
                <div
                  key={notif.id}
                  className="p-4 bg-slate-50 rounded-xl border border-slate-200"
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        {getStatusBadge(notif.statut)}
                        <span className="text-xs text-slate-500">
                          {formatDate(notif.date_creation)}
                        </span>
                      </div>
                      <p className="font-bold text-slate-900">{notif.sujet}</p>
                      <p className="text-sm text-slate-600">
                        À: {notif.destinataire_nom} ({notif.destinataire_email})
                      </p>
                      {notif.erreur_message && (
                        <p className="text-xs text-red-600 mt-1">
                          Erreur: {notif.erreur_message}
                        </p>
                      )}
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteNotification(notif.id)}
                      className="text-red-500 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              ))
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default NotificationsPanel;
